import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse, IFormular } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import type { Formular, FormularDetails, FormularPdfImport, ModuleId } from '@/types/formular'
import { afterReceiving } from './hooks' 

const resource = 'formulars'

const getAll = (axios: IFactoryFunctionRequest) => ({ firmId, moduleId }: { firmId: string, moduleId: number }): IFactoryResponse<Array<IFormular>> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/firm/${moduleId}/${firmId}`,
  })
}

const listRecruitment = (axios: IFactoryFunctionRequest<any, any>) => (options: object): IFactoryResponse<Array<IFormular>> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/formulars/v2/recruitment`,
    query: options,
  })
}

/**
 * could be not paginated with query option paginated = false
 */
const getPaginated = (axios: IFactoryFunctionRequest<any, any>) => ({ moduleId, options }: { moduleId: number, options: any }): IFactoryResponse<Array<IFormular>> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${moduleId}`,
    query: options,
    afterReceivingHook: afterReceiving
  })
}

// V2 API Routes

/**
 * Get formular details by ID
 */
const getDetails = (axios: IFactoryFunctionRequest<FormularDetails, void>) => (id: string): IFactoryResponse<FormularDetails> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/details`,
  })
}

/**
 * Create a new formular
 */
const create = (axios: IFactoryFunctionRequest<Formular, Partial<Formular>>) => (data: Partial<Formular>): IFactoryResponse<Formular> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data,
  })
}

/**
 * Update an existing formular
 */
const update = (axios: IFactoryFunctionRequest<Formular, Partial<Formular>>) => (id: string, data: Partial<Formular>): IFactoryResponse<Formular> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
    data,
  })
}

/**
 * Soft delete a formular
 */
const remove = (axios: IFactoryFunctionRequest<{ message: string }, void>) => (id: string): IFactoryResponse<{ message: string }> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
  })
}

/**
 * Duplicate a formular
 */
const duplicate = (axios: IFactoryFunctionRequest<Formular, { display?: string; evaluationCreationDefaults?: Formular['evaluationCreationDefaults'] }>) => (id: string, data?: { display?: string; evaluationCreationDefaults?: Formular['evaluationCreationDefaults'] }): IFactoryResponse<Formular> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/duplicate`,
    data,
  })
}

/**
 * Copy evaluation creation defaults from a source form onto other evaluation forms
 */
const copyEvaluationCreationDefaults = (
  axios: IFactoryFunctionRequest<{ updatedCount: number; evaluationCreationDefaults: Formular['evaluationCreationDefaults'] }, { formularIds: string[]; evaluationCreationDefaults?: Formular['evaluationCreationDefaults'] }>,
) => (
  id: string,
  data: { formularIds: string[]; evaluationCreationDefaults?: Formular['evaluationCreationDefaults'] },
): IFactoryResponse<{ updatedCount: number; evaluationCreationDefaults: Formular['evaluationCreationDefaults'] }> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/copy-evaluation-creation-defaults`,
    data,
  })
}

/**
 * Structure a form template from a PDF with AI (not saved: the builder shows it for review)
 */
const importPdf = (axios: IFactoryFunctionRequest<FormularPdfImport, FormData>) => (file: File, moduleId: ModuleId): IFactoryResponse<FormularPdfImport> => {
  const data = new FormData()
  data.append('file', file)
  data.append('moduleId', String(moduleId))
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/import-pdf`,
    data,
  })
}

/**
 * Download formular as PDF
 */
const downloadPdf = (axios: IFactoryFunctionRequest<{ base64: string }, void>) => (id: string): IFactoryResponse<{ base64: string }> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${id}/downloadPdf`,
  })
}

export default (axios: AxiosInstance) => ({
  getAll: getAll(useAxios(axios)),
  getPaginated: getPaginated(useAxios(axios)),
  listRecruitment: listRecruitment(useAxios(axios)),
  // V2 API
  getDetails: getDetails(useAxios(axios)),
  create: create(useAxios(axios)),
  update: update(useAxios(axios)),
  remove: remove(useAxios(axios)),
  duplicate: duplicate(useAxios(axios)),
  copyEvaluationCreationDefaults: copyEvaluationCreationDefaults(useAxios(axios)),
  downloadPdf: downloadPdf(useAxios(axios)),
  importPdf: importPdf(useAxios(axios)),
})
